import React, { useCallback, useDeferredValue, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import classnames from 'classnames';
import Input from 'reactstrap/lib/Input';
import Button from 'reactstrap/lib/Button';
/* Constants */
import { OPEN_ANIMATION } from 'site-modules/shared/components/drawer/drawer';
import {
  CLEAR_INPUT,
  AUTOSIZED_AREA_LENGTH,
  LOOKING_FOR_PLACEHOLDER,
} from 'site-modules/shared/constants/global-search/global-search';
/* Hooks */
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
/* Components */
import { GlobalSearchResults } from 'site-modules/shared/components/inventory/global-search/global-search-results/global-search-results';

import './global-search-dialog-content.scss';

export function GlobalSearchDialogContent({
  searchId,
  creativeId,
  fastMatcherData,
  query,
  onSearchFieldKeydown,
  onInputChange,
  onQueryCancel,
  onDrawerClose,
  onLinkClick,
  isFastMatcherLoading,
  withLlmSearchDisabled,
}) {
  const deferredQuery = useDeferredValue(query);
  const inputRef = useRef();
  const [drawerAnimationTimeout, clearDrawerAnimationTimeout] = useTimeout();

  useEffect(() => {
    drawerAnimationTimeout(() => {
      inputRef.current?.focus();
    }, OPEN_ANIMATION);

    return () => {
      clearDrawerAnimationTimeout();
    };
  }, [clearDrawerAnimationTimeout, drawerAnimationTimeout]);

  const handleInputChange = useCallback(
    async ({ target: { value } }) => {
      await onInputChange(value);
    },
    [onInputChange]
  );

  const handleQueryCancel = useCallback(async () => {
    await onQueryCancel();
    inputRef.current?.focus();
  }, [onQueryCancel]);

  return (
    <div className="global-search-dialog-content bg-white p-1">
      <div data-tracking-parent={creativeId}>
        <div className="d-flex align-items-center mb-1">
          <Button onClick={onDrawerClose} className="p-0 me-1 border-0 background-none" aria-label="Cancel search">
            <i className="text-cool-gray-50 icon-arrow-left3" aria-hidden />
          </Button>
          <div className="global-search-input pos-r w-100">
            <Input
              type="text"
              id="global-search-input"
              name="global-search-input"
              autoComplete="off"
              placeholder={LOOKING_FOR_PLACEHOLDER}
              className={classnames('search-field h-100 size-16 bg-cool-gray-80 rounded-12 border-0', {
                'pt-1_75 ps-0_75 pe-2_5 with-text': !!query,
                'py-0_5 px-0_75': !query,
              })}
              value={query}
              onChange={handleInputChange}
              maxLength={AUTOSIZED_AREA_LENGTH}
              innerRef={inputRef}
              onKeyDown={onSearchFieldKeydown}
            />
            <label className="search-label" htmlFor="global-search-input">
              {LOOKING_FOR_PLACEHOLDER}
            </label>
            {!!query && (
              <Button
                onClick={handleQueryCancel}
                className="query-cancel-btn pos-a p-0 border-0 background-none d-flex justify-content-center align-items-center"
              >
                <i className="text-cool-gray-50 icon-cross2 small" role="img" aria-label={CLEAR_INPUT} />
              </Button>
            )}
          </div>
        </div>
        <GlobalSearchResults
          searchQuery={deferredQuery}
          fastMatcherData={fastMatcherData}
          isFastMatcherLoading={isFastMatcherLoading}
          searchId={searchId}
          isMobile
          withLlmSearchDisabled={withLlmSearchDisabled}
          creativeId={creativeId}
          onLinkClick={onLinkClick}
        />
      </div>
    </div>
  );
}

GlobalSearchDialogContent.propTypes = {
  searchId: PropTypes.string,
  creativeId: PropTypes.string,
  query: PropTypes.string,
  fastMatcherData: PropTypes.shape({
    delta: PropTypes.string,
    struct: PropTypes.shape({}),
  }),
  onSearchFieldKeydown: PropTypes.func,
  onInputChange: PropTypes.func,
  onQueryCancel: PropTypes.func,
  onDrawerClose: PropTypes.func,
  onLinkClick: PropTypes.func,
  isFastMatcherLoading: PropTypes.bool,
  withLlmSearchDisabled: PropTypes.bool,
};

GlobalSearchDialogContent.defaultProps = {
  searchId: '',
  creativeId: '',
  query: '',
  fastMatcherData: null,
  isFastMatcherLoading: false,
  onSearchFieldKeydown: noop,
  onInputChange: noop,
  onQueryCancel: noop,
  onDrawerClose: noop,
  onLinkClick: noop,
  withLlmSearchDisabled: false,
};
